import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { LANGUAGE_LABELS, type Language } from '@/admin/languages';
import { GuidesToolbar } from '@/admin/publishing/GuidesToolbar';
import { StatusBadge } from '@/admin/publishing/StatusBadge';
import { createTablePage } from '@/admin/ui/createTablePage';
import { readTableSearch } from '@/admin/ui/tableSearch';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type AdminStudyGuideSummary = components['schemas']['AdminStudyGuideSummary'];

type GuidesSearch = { language?: Language; page?: number };

const routeApi = getRouteApi('/admin/bible/guides/');
const LANGUAGE_CODES = Object.keys(LANGUAGE_LABELS) as Language[];

const columnHelper = createColumnHelper<AdminStudyGuideSummary>();

const columns = [
  columnHelper.accessor('order', {
    header: () => i18n.t('admin.common.order'),
  }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  columnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (c) => i18n.t(LANGUAGE_LABELS[c.getValue()]),
  }),
  columnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
    cell: (c) => <StatusBadge status={c.getValue()} />,
  }),
];

const GuideList = createTablePage<
  AdminStudyGuideSummary,
  '/admin/bible/guides/$guideId'
>({
  name: 'admin.nav.bible',
  urlState: true,
  useData: (_page, _search, extras) => {
    // Cast: the route names this component, so inferring its search is circular.
    const { language } = routeApi.useSearch() as GuidesSearch;
    return api.adminListStudyGuides.useQuery({
      query: {
        language,
        limit: extras.paging.limit,
        offset: extras.paging.offset,
      },
    });
  },
  columns,
  itemBaseUrl: '/admin/bible/guides/$guideId',
  getParams: (row) => ({ guideId: String(row.id) }),
  actions: <GuidesToolbar />,
});

export const Route = createFileRoute('/admin/bible/guides/')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (s: Record<string, unknown>): GuidesSearch => {
    const { page } = readTableSearch(s);
    return {
      language: LANGUAGE_CODES.find((l) => l === s.language),
      page: page > 0 ? page + 1 : undefined,
    };
  },
  component: GuideList,
});
